import React, { useState, useEffect } from 'react';
import { TransactionResult, transactionsApi } from '../../api/transactions';
import { toast } from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface TransactionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionResult | null;
  onSuccess: () => void;
}

export const TransactionEditModal: React.FC<TransactionEditModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    counterpartyName: '',
    counterpartyPhone: '',
    counterpartyAddress: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (transaction) {
      setFormData({
        counterpartyName: transaction.counterpartyName || '',
        counterpartyPhone: transaction.counterpartyPhone || '',
        counterpartyAddress: transaction.counterpartyAddress || '',
        notes: transaction.notes || '',
      });
    }
  }, [transaction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction) return;
    setIsSubmitting(true);

    try {
      await transactionsApi.updateTransaction(transaction.id, formData);
      toast.success('Transaction updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      toast.error(error.response?.data?.message || 'Failed to update transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <BrandModal open={isOpen && !!transaction} onClose={onClose} maxWidthClass="max-w-2xl">
      {transaction ? (
        <>
          <BrandModalHeader
            title={`Edit Transaction - ${transaction.invoiceNumber}`}
            onClose={onClose}
          />
          <form onSubmit={handleSubmit}>
            <BrandModalBody>
              <div className="space-y-4">
                <div className="bg-cyan-50 p-4 rounded-md">
                  <p className="text-sm text-cyan-800">
                    Note: Only counterparty information and notes can be edited. Financial amounts and accounting entries cannot be modified after creation.
                  </p>
                </div>

                <div>
                  <label htmlFor="counterpartyName" className="block text-sm font-medium text-slate-700 mb-1">
                    Counterparty Name *
                  </label>
                  <input
                    type="text"
                    id="counterpartyName"
                    value={formData.counterpartyName}
                    onChange={(e) => handleChange('counterpartyName', e.target.value)}
                    required
                    className="input w-full"
                  />
                </div>

                <div>
                  <label htmlFor="counterpartyPhone" className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="counterpartyPhone"
                    value={formData.counterpartyPhone}
                    onChange={(e) => handleChange('counterpartyPhone', e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label htmlFor="counterpartyAddress" className="block text-sm font-medium text-slate-700 mb-1">
                    Address
                  </label>
                  <textarea
                    id="counterpartyAddress"
                    value={formData.counterpartyAddress}
                    onChange={(e) => handleChange('counterpartyAddress', e.target.value)}
                    rows={3}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={3}
                    className="input w-full"
                  />
                </div>
              </div>
            </BrandModalBody>
            <BrandModalFooter>
              <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="btn btn-primary text-sm">
                {isSubmitting ? 'Updating...' : 'Update Transaction'}
              </button>
            </BrandModalFooter>
          </form>
        </>
      ) : null}
    </BrandModal>
  );
};
